import moment from 'moment';

import {
  MailMessageFieldsFragment,
  MailThreadAction,
  MailView,
} from 'core/graphql';
import { PATHS } from 'router/config';

/**
 * ТЗ 0122 — почтовый клиент ERP: чистая логика без React (адрес страницы,
 * папки, цвета участников, лента «кто → кому», пресеты времени, ошибки).
 * Проверяется jest-ом (`__tests__/mailModel.test.ts`).
 */

/* ── Папки и адрес страницы ──────────────────────────────────────────── */

export type MailViewKey =
  | 'inbox'
  | 'snoozed'
  | 'starred'
  | 'drafts'
  | 'scheduled'
  | 'sent'
  | 'all'
  | 'spam'
  | 'trash'
  | 'label'
  | 'needs_reply'
  | 'urgent'
  | 'awaiting'
  | 'deadline';

/** Папки левой колонки — в порядке §5. */
export const MAIL_FOLDERS: MailViewKey[] = [
  'inbox',
  'snoozed',
  'starred',
  'drafts',
  'scheduled',
  'sent',
  'all',
  'spam',
  'trash',
];

const VIEW_ENUM: Record<MailViewKey, MailView> = {
  inbox: MailView.Inbox,
  snoozed: MailView.Snoozed,
  starred: MailView.Starred,
  drafts: MailView.Drafts,
  scheduled: MailView.Scheduled,
  sent: MailView.Sent,
  all: MailView.All,
  spam: MailView.Spam,
  trash: MailView.Trash,
  label: MailView.Label,
  needs_reply: MailView.NeedsReply,
  urgent: MailView.Urgent,
  awaiting: MailView.Awaiting,
  deadline: MailView.Deadline,
};

/** Раздел «ИИ» левой колонки (ТЗ 0122, «ИИ в почте»). */
export const MAIL_AI_VIEWS: MailViewKey[] = [
  'needs_reply',
  'urgent',
  'awaiting',
  'deadline',
];

export const mailViewEnum = (view: MailViewKey): MailView => VIEW_ENUM[view];

const isViewKey = (value: string): value is MailViewKey =>
  Object.prototype.hasOwnProperty.call(VIEW_ENUM, value);

/** Что открыто на странице «Почта» — всё живёт в адресе (`/mail?…`). */
export interface IMailLocation {
  /** Ящик; `null` — «все мои ящики». */
  box: string | null;
  view: MailViewKey;
  /** Gmail-id метки для `view = label`. */
  label: string | null;
  thread: string | null;
  search: string;
  /** Ссылка из коммуникатора: ящик по адресу (id странице неизвестен). */
  boxEmail: string | null;
  /** «Написать» из контактов: адресат нового письма. */
  compose: string | null;
  /** Фильтр по категории ИИ (`STUDY`, `PAYMENT`, …). */
  category: string | null;
  /**
   * ТЗ 0121 для почты: экран телефона «ящики и папки» (`?pane=folders`).
   * Пишется только на телефоне; компьютер его не читает.
   */
  pane: MailPane | null;
}

/** Отдельные экраны телефона, которых нет в адресе по-другому. */
export type MailPane = 'folders';

export const DEFAULT_MAIL_LOCATION: IMailLocation = {
  box: null,
  view: 'inbox',
  label: null,
  thread: null,
  search: '',
  boxEmail: null,
  compose: null,
  category: null,
  pane: null,
};

export const parseMailLocation = (search: string): IMailLocation => {
  const params = new URLSearchParams(search);
  const rawView = (params.get('view') || '').toLowerCase();
  const label = params.get('label') || null;
  let view: MailViewKey = isViewKey(rawView) ? rawView : 'inbox';
  if (view === 'label' && !label) view = 'inbox';
  return {
    box: params.get('box') || null,
    view,
    label: view === 'label' ? label : null,
    thread: params.get('thread') || null,
    search: params.get('q') || '',
    boxEmail: params.get('boxEmail') || null,
    // `?compose` без значения — тоже «открыть окно письма» (пустое).
    compose: params.has('compose') ? params.get('compose') || '' : null,
    category: (params.get('cat') || '').toUpperCase() || null,
    pane: params.get('pane') === 'folders' ? 'folders' : null,
  };
};

/** Адрес страницы «Почта» (путь — только из `PATHS`). */
export const mailHref = (location: Partial<IMailLocation> = {}): string => {
  const params = new URLSearchParams();
  if (location.box) params.set('box', location.box);
  if (location.view && location.view !== 'inbox') {
    params.set('view', location.view);
  }
  if (location.view === 'label' && location.label) {
    params.set('label', location.label);
  }
  if (location.thread) params.set('thread', location.thread);
  if (location.search) params.set('q', location.search);
  if (location.boxEmail) params.set('boxEmail', location.boxEmail);
  if (typeof location.compose === 'string') {
    params.set('compose', location.compose);
  }
  if (location.category) params.set('cat', location.category.toLowerCase());
  if (location.pane) params.set('pane', location.pane);
  const query = params.toString();
  return query ? `${PATHS.mail}?${query}` : PATHS.mail;
};

/**
 * ТЗ 0121 для почты — экраны телефона по одному: папки → список → тред.
 * Открытый тред важнее папок: `?thread=` и `?pane=folders` вместе (ссылка,
 * собранная руками) — это тред.
 */
export type MailMobileStep = 'folders' | 'list' | 'thread';

export const mailMobileStep = (
  location: Pick<IMailLocation, 'thread' | 'pane'>,
): MailMobileStep => {
  if (location.thread) return 'thread';
  if (location.pane === 'folders') return 'folders';
  return 'list';
};

/**
 * Куда ведёт «Назад» на странице, если запись истории положил не шаг
 * стека (открыли по ссылке): из треда и из папок — в список той же папки.
 */
export const mailMobileParent = (
  location: IMailLocation,
): Partial<IMailLocation> => ({
  ...location,
  thread: null,
  pane: null,
});

/* ── Цвета и подписи ─────────────────────────────────────────────────── */

/**
 * Цвета ящиков и участников — цвета ИЗ ДАННЫХ (точка ящика, чип участника),
 * одни на обе темы: текст на них — белый, фон — насыщенный 600-й тон.
 */
export const MAIL_PALETTE = [
  '#2563eb', // theme-fixed
  '#16a34a', // theme-fixed
  '#d97706', // theme-fixed
  '#9333ea', // theme-fixed
  '#db2777', // theme-fixed
  '#0891b2', // theme-fixed
  '#dc2626', // theme-fixed
  '#4f46e5', // theme-fixed
  '#65a30d', // theme-fixed
  '#0d9488', // theme-fixed
];

export const mailboxColor = (colorIndex: number): string =>
  MAIL_PALETTE[
    ((colorIndex % MAIL_PALETTE.length) + MAIL_PALETTE.length) %
      MAIL_PALETTE.length
  ];

/** Стабильный цвет участника по адресу (одинаковый во всех тредах). */
/**
 * Цвет НАШЕЙ стороны (ящик ERP, сотрудник) — фирменный синий; внешним
 * участникам он не выдаётся никогда (ТЗ 0122, проверка глазами).
 */
export const OUR_COLOR = '#2563eb'; // theme-fixed

/** Цвета внешних участников — без синих оттенков нашей стороны. */
export const EXTERNAL_PALETTE = MAIL_PALETTE.filter(
  (color) => color !== OUR_COLOR && color !== '#4f46e5', // theme-fixed
);

/** Стабильный цвет внешнего адреса вне треда (строка списка, контакт). */
export const participantColor = (email: string): string => {
  const value = (email || '').toLowerCase();
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 2147483647;
  }
  return EXTERNAL_PALETTE[hash % EXTERNAL_PALETTE.length];
};

/**
 * Цвета участников ОДНОГО треда: наша сторона — `OUR_COLOR`, внешние —
 * по порядку появления (а не по хешу), поэтому двое внешних в треде не
 * получают один цвет, пока палитры хватает.
 */
export const threadParticipantColors = (
  participants: readonly { email: string }[],
  ours: ReadonlySet<string>,
): Map<string, string> => {
  const out = new Map<string, string>();
  let next = 0;
  participants.forEach(({ email }) => {
    const key = (email || '').toLowerCase();
    if (!key || out.has(key)) return;
    if (ours.has(key)) {
      out.set(key, OUR_COLOR);
      return;
    }
    out.set(key, EXTERNAL_PALETTE[next % EXTERNAL_PALETTE.length]);
    next += 1;
  });
  return out;
};

export interface IMailAddress {
  email: string;
  name?: string | null;
}

export const addressLabel = (address: IMailAddress): string =>
  (address.name || '').trim() || address.email;

/** Короткое имя: «Jan Kowalski» → «Jan K.», адрес → часть до @. */
const LETTER = /^[A-Za-z\u00C0-\u024F\u0400-\u04FF]/;

/** Первая буква слова; слово не с буквы («(demo)», «2026») — не в счёт. */
const firstLetter = (word: string): string =>
  LETTER.test(word) ? word.charAt(0) : '';

/**
 * Короткое имя: «Jan Kowalski» → «Jan K.»; слова не с буквы («(demo)»)
 * пропускаются — «Urząd Pracy (demo)» → «Urząd P.», а не «Urząd (.».
 * Нет имени — часть адреса до @.
 */
export const shortName = (address: IMailAddress): string => {
  const name = (address.name || '').trim();
  if (!name) return (address.email || '').split('@')[0];
  const words = name.split(/\s+/).filter((word) => LETTER.test(word));
  if (words.length < 2) return name;
  return `${words[0]} ${words[words.length - 1].charAt(0)}.`;
};

export const initials = (address: IMailAddress): string => {
  const letters = (address.name || '')
    .split(/\s+/)
    .map(firstLetter)
    .filter(Boolean);
  if (letters.length) {
    const last = letters.length > 1 ? letters[letters.length - 1] : '';
    return `${letters[0]}${last}`.toUpperCase();
  }
  return (firstLetter(address.email || '') || '@').toUpperCase();
};

/** Время в списке: сегодня — часы, в этом году — день и месяц, иначе дата. */
export const listTime = (
  iso: string,
  now: moment.Moment = moment(),
): string => {
  const value = moment(iso);
  if (!value.isValid()) return '';
  if (value.isSame(now, 'day')) return value.format('HH:mm');
  if (value.isSame(now, 'year')) return value.format('DD.MM');
  return value.format('DD.MM.YYYY');
};

export const fullTime = (iso: string): string => {
  const value = moment(iso);
  return value.isValid() ? value.format('DD.MM.YYYY HH:mm') : '';
};

/* ── Адреса в полях «Кому / Копия» ───────────────────────────────────── */

const EMAIL_RE = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]{2,}$/;

export const isValidEmail = (value: string): boolean =>
  EMAIL_RE.test((value || '').trim());

/**
 * Адрес из `?compose=…` — только если это настоящий адрес (контакты →
 * «Написать»). `new`, `1`, пусто и прочее — окно письма без получателя,
 * а не красный чип «new» в «Кому».
 */
export const composeRecipient = (value: string | null): string | null => {
  const email = (value || '').trim();
  return email && isValidEmail(email) ? email : null;
};

/**
 * Вставленный / набранный текст → адреса: «Jan <jan@x.pl>, ola@y.pl; …».
 * Невалидные куски возвращаются отдельно — поле подсветит их.
 */
export const parseAddressInput = (
  raw: string,
): { addresses: IMailAddress[]; invalid: string[] } => {
  const addresses: IMailAddress[] = [];
  const invalid: string[] = [];
  const chunks = (raw || '')
    .split(/[,;\n]+/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);
  chunks.forEach((chunk) => {
    const angle = chunk.match(/^(.*)<([^>]+)>\s*$/);
    if (angle) {
      const email = angle[2].trim().toLowerCase();
      const name = angle[1].trim().replace(/^"|"$/g, '');
      if (isValidEmail(email)) addresses.push({ email, name: name || null });
      else invalid.push(chunk);
      return;
    }
    chunk
      .split(/\s+/)
      .filter(Boolean)
      .forEach((word) => {
        const email = word.toLowerCase();
        if (isValidEmail(email)) addresses.push({ email, name: null });
        else invalid.push(word);
      });
  });
  return { addresses, invalid };
};

/** Список без повторов адреса (первый выигрывает). */
export const uniqueAddresses = <T extends IMailAddress>(list: T[]): T[] => {
  const seen = new Set<string>();
  return list.filter((item) => {
    const key = item.email.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/* ── Лента «кто → кому» ──────────────────────────────────────────────── */

export type IMailMessage = MailMessageFieldsFragment;

export interface IMailActionLog {
  id: string;
  action: string;
  userName: string;
  createdAt: string;
}

export type MailTimelineItem =
  | { kind: 'message'; key: string; at: string; message: IMailMessage }
  | { kind: 'action'; key: string; at: string; log: IMailActionLog }
  | { kind: 'more'; key: string; at: string; count: number };

const ts = (iso: string): number => moment(iso).valueOf() || 0;

/**
 * Письма и журнал действий одной лентой по времени. Старые прочитанные
 * письма — со второго до предпоследнего, подряд, пока не встретится
 * непрочитанное — сворачиваются в строку «ещё N писем» (если их хотя бы
 * два и `expandAll` выключен). Действия внутри свёрнутого отрезка тоже
 * прячутся. Черновики в ленту не идут — они в редакторе ответа.
 */
export const buildTimeline = (
  messages: readonly IMailMessage[],
  actions: readonly IMailActionLog[],
  expandAll = false,
): MailTimelineItem[] => {
  const sent = messages
    .filter((message) => !message.isDraft)
    .slice()
    .sort((a, b) => ts(a.sentAt) - ts(b.sentAt));
  const hidden = new Set<string>();
  let hiddenFrom: string | null = null;
  let hiddenTo: string | null = null;
  if (!expandAll && sent.length > 3) {
    const run: IMailMessage[] = [];
    for (let i = 1; i < sent.length - 1; i += 1) {
      if (sent[i].isUnread) break;
      run.push(sent[i]);
    }
    if (run.length >= 2) {
      run.forEach((message) => hidden.add(message.id));
      hiddenFrom = run[0].sentAt;
      hiddenTo = run[run.length - 1].sentAt;
    }
  }
  const items: MailTimelineItem[] = [];
  sent.forEach((message) => {
    if (hidden.has(message.id)) return;
    items.push({
      kind: 'message',
      key: `m-${message.id}`,
      at: message.sentAt,
      message,
    });
  });
  actions.forEach((log) => {
    const at = ts(log.createdAt);
    if (hiddenFrom && hiddenTo && at >= ts(hiddenFrom) && at <= ts(hiddenTo)) {
      return;
    }
    items.push({ kind: 'action', key: `a-${log.id}`, at: log.createdAt, log });
  });
  if (hiddenFrom) {
    items.push({
      kind: 'more',
      key: 'more',
      at: hiddenFrom,
      count: hidden.size,
    });
  }
  const order = { message: 0, more: 0, action: 1 } as const;
  return items.sort(
    (a, b) => ts(a.at) - ts(b.at) || order[a.kind] - order[b.kind],
  );
};

/** Какие письма раскрыты сразу: непрочитанные и последнее. */
export const initiallyExpanded = (
  messages: readonly IMailMessage[],
): Set<string> => {
  const sent = messages.filter((message) => !message.isDraft);
  const out = new Set<string>();
  sent.forEach((message) => {
    if (message.isUnread) out.add(message.id);
  });
  const last = sent
    .slice()
    .sort((a, b) => ts(a.sentAt) - ts(b.sentAt))
    .pop();
  if (last) out.add(last.id);
  return out;
};

/** Черновик ответа в треде (последний), если есть. */
export const threadDraft = (
  messages: readonly IMailMessage[],
): IMailMessage | null =>
  messages
    .filter((message) => message.isDraft)
    .slice()
    .sort((a, b) => ts(a.sentAt) - ts(b.sentAt))
    .pop() || null;

/* ── Пресеты времени ─────────────────────────────────────────────────── */

export type MailTimePreset = 'hour' | 'later' | 'tomorrow' | 'monday';

/**
 * «Через час», «Сегодня позже» (+3 ч), «Завтра 9:00», «Понедельник 9:00»
 * (ближайший СЛЕДУЮЩИЙ понедельник: в понедельник — через неделю).
 */
export const presetTime = (
  preset: MailTimePreset,
  now: moment.Moment = moment(),
): moment.Moment => {
  const base = now.clone().seconds(0).milliseconds(0);
  switch (preset) {
    case 'hour':
      return base.add(1, 'hour');
    case 'later':
      return base.add(3, 'hours');
    case 'tomorrow':
      return base.add(1, 'day').hours(9).minutes(0);
    case 'monday': {
      const days = (8 - base.isoWeekday()) % 7 || 7;
      return base.add(days, 'days').hours(9).minutes(0);
    }
    default:
      return base;
  }
};

/* ── Действия и ошибки ───────────────────────────────────────────────── */

export type MailActionKey =
  | 'done'
  | 'archive'
  | 'inbox'
  | 'read'
  | 'unread'
  | 'star'
  | 'unstar'
  | 'trash'
  | 'untrash'
  | 'spam'
  | 'notspam';

const ACTION_ENUM: Record<MailActionKey, MailThreadAction> = {
  done: MailThreadAction.Done,
  archive: MailThreadAction.Archive,
  inbox: MailThreadAction.Inbox,
  read: MailThreadAction.Read,
  unread: MailThreadAction.Unread,
  star: MailThreadAction.Star,
  unstar: MailThreadAction.Unstar,
  trash: MailThreadAction.Trash,
  untrash: MailThreadAction.Untrash,
  spam: MailThreadAction.Spam,
  notspam: MailThreadAction.Notspam,
};

export const mailActionEnum = (action: MailActionKey): MailThreadAction =>
  ACTION_ENUM[action];

/**
 * Уходит ли тред из текущего списка после действия — тогда строка
 * пропадает сразу (до перечитывания).
 */
export const actionRemovesFromView = (
  action: MailActionKey,
  view: MailViewKey,
): boolean => {
  if (action === 'trash') return view !== 'trash';
  if (action === 'untrash') return view === 'trash';
  if (action === 'spam') return view !== 'spam';
  if (action === 'notspam') return view === 'spam';
  if (action === 'done' || action === 'archive') {
    return view === 'inbox' || view === 'needs_reply' || view === 'urgent';
  }
  if (action === 'unstar') return view === 'starred';
  return false;
};

/** Коды ошибок сервера (§4), у которых есть перевод `mail.errors.*`. */
export const MAIL_CLIENT_ERROR_CODES = [
  'NO_ACCESS',
  'NO_SEND',
  'GMAIL_ERROR',
  'GMAIL_SCOPE',
  'RATE_LIMIT',
  'NOT_FOUND',
  'EMPTY_RECIPIENTS',
  'INVALID_EMAIL',
  'TOO_LARGE',
  'EXPIRED',
  'UNAVAILABLE',
  'AI_OFF',
  'AI_BUDGET',
  'PAST_SEND_AT',
  'INVALID',
  'DAILY_LIMIT',
] as const;

export const mailErrorKey = (code?: string | null): string | null => {
  const value = (code || '').toUpperCase();
  return (MAIL_CLIENT_ERROR_CODES as readonly string[]).includes(value)
    ? `mail.errors.${value}`
    : null;
};

/** Результат мутации почты: `{ ok, error, errorCode }`. */
export interface IMailResult {
  ok: boolean;
  error?: string | null;
  errorCode?: string | null;
}

export const mailFailure = (caught: unknown): IMailResult => ({
  ok: false,
  error: caught instanceof Error ? caught.message : null,
  errorCode: 'UNAVAILABLE',
});

/* ── Связанные сущности ERP ──────────────────────────────────────────── */

export type MailLinkedKind =
  | 'student'
  | 'teacher'
  | 'lead'
  | 'staff'
  | 'contract';

export const MAIL_CONTACT_KINDS = [
  'student',
  'teacher',
  'lead',
  'staff',
  'other',
] as const;

export type MailContactKind = typeof MAIL_CONTACT_KINDS[number];
